// T-020: gestione menù del business attivo.
//
// In questa prima iterazione la pagina è "consultativa" + add semplice:
//  - Mostra business attivo + slug pubblico + bottone QR.
//  - Lista categorie con i loro item (read-only ma con pulsanti + per add).
//  - Add categoria / add item con modale rapido (nome, descrizione, prezzo,
//    allergeni).
//
// Le evoluzioni future (modifica/elimina item, foto, varianti) seguono in
// T-021 quando arrivano gli ordini.

import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonPage,
  IonSelect,
  IonSegment,
  IonSegmentButton,
  useIonViewWillEnter,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  IonText,
  IonTitle,
  IonToast,
  IonToggle,
  IonToolbar,
} from '@ionic/react';
import {
  calendarOutline,
  checkmarkCircleOutline,
  colorPaletteOutline,
  downloadOutline,
  linkOutline,
  logoWhatsapp,
  mailOutline,
  phonePortraitOutline,
  printOutline,
  qrCodeOutline,
  ribbonOutline,
  sparklesOutline,
  storefrontOutline,
} from 'ionicons/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import { Capacitor } from '@capacitor/core';
import { Share } from '../../lib/adapters/share/index.js';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { generateMenuPdf } from '../../lib/pdf/menuPdf.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { getSupabase } from '../../lib/supabase.js';
import { toUserMessage } from '../../lib/errors.js';
import { useCelebrate } from '../../components/CelebrationToast.js';
import { createPrinterAdapter, PREVIEW_PRINTER_ID } from '../../lib/adapters/printers/index.js';
import { resolveDefaultPrinterId } from '../../lib/printers/device-printers-core.js';
import { useDevicePrinters } from '../../lib/printers/devicePrinters.js';
import { canEditMenu, ALLERGEN_LABELS_IT, type Allergen, AllergenSchema, type LabelTemplate } from '@trackeat/shared';
import { openBookingPage } from '../../lib/booking.js';
import { menuTables, menuTypeFromSearch, menuViewUrl, menuItemPriceLabel, type MenuType } from './menu/menuType.js';
import { hasPublishedCustomMenu } from './menu-access.js';

interface BusinessRow {
  id: string;
  name: string;
  menu_public_slug: string | null;
  menu_published: boolean;
  menu_theme: {
    chef_name?: string;
    chef_subtitle?: string;
    tagline?: string;
    location?: string;
    palette?: { primary?: string; accent?: string; ink?: string };
  } | null;
}
interface TableRow {
  id: string;
  label: string;
  qr_token: string;
  sort_order: number;
  active: boolean;
}
interface CategoryRow {
  id: string;
  name: string;
  icon: string | null;
  sort_order: number;
  visible: boolean;
  section?: 'food' | 'bar';
}
interface ItemRow {
  id: string;
  category_id: string;
  name: string;
  description: string | null;
  price_cents: number | null;
  price_unit?: string | null;
  allergens: string[];
  station: 'cucina' | 'bar';
  sort_order: number;
  visible: boolean;
  out_of_stock: boolean;
}
// T-026: costruisce on-the-fly un LabelTemplate per stampare il QR del menu
// (o del tavolo) sull'etichetta selezionata. Due layout in base alle
// proporzioni dell'etichetta:
//
//  - **Landscape** (W > H): QR a sinistra, testi del locale a destra. Sfrutta
//    tutta la larghezza per fare "biglietto da visita" elegante (es. 50×30 mm).
//  - **Square / Portrait** (W <= H): QR centrato in alto + 2-3 righe di testo
//    sotto (nome locale, tagline). Utile per etichette tipo 40×40 o 50×80.
//
// I testi sono presi dal `menu_theme` del business; se mancanti si usano
// fallback dignitosi (solo il nome + "scansiona per il menu").
function buildQrLabelTemplate(args: {
  url: string;
  sizeMm: { width: number; height: number };
  businessName: string;
  tagline?: string;
  location?: string;
  tableLabel?: string;
}): LabelTemplate {
  const { url, sizeMm, businessName, tagline, location, tableLabel } = args;
  const W = sizeMm.width;
  const H = sizeMm.height;
  const margin = 1.5;
  const elements: LabelTemplate['elements'] = [];

  const landscape = W >= H + 4; // soglia per evitare 50×40 borderline
  if (landscape) {
    // ── Layout landscape (es. 50×30 mm) ────────────────────────────────
    // Sinistra: QR alla MASSIMA dimensione. Quando l'etichetta è di un
    // TAVOLO (tableLabel presente) il QR viene shiftato +2mm verso il
    // basso per dare più aria sopra (richiesta utente T-028).
    // Destra (centrata verticalmente come blocco):
    //   - SENZA tableLabel:    Àncora + nome (2 righe)
    //   - CON tableLabel:      "Tavolo N" + Àncora (più piccola) + nome (1 riga)
    const scale = H / 30;
    const isTableLabel = !!tableLabel;

    // QR a sinistra. Per i tavoli: shift verticale di 2mm.
    const qrShiftY = isTableLabel ? 2 : 0;
    const qrSize = Math.max(10, H - 2 * margin - qrShiftY);
    const qrX = margin;
    const qrY = margin + qrShiftY;
    elements.push({
      id: 'qr', type: 'qr',
      x_mm: qrX, y_mm: qrY,
      width_mm: qrSize, height_mm: qrSize,
      static_value: url, rotation: 0,
    });

    // Colonna destra
    const textX = qrX + qrSize + 1.5;
    const textW = Math.max(8, W - textX - margin);

    if (isTableLabel) {
      // ── Layout TAVOLO ───────────────────────────────────────────────
      // Blocco destro centrato verticalmente:
      //   "Tavolo N"   (font grande, bold)
      //   @anchor      (logo, media)
      //   "La Terrazza di Giò"  (nome locale, una riga, auto-scaled)
      const tavoloText = `Tavolo ${tableLabel}`;

      // Auto-scale font "Tavolo N" — vogliamo il più grande possibile
      // dentro textW. Helvetica bold a 203 DPI: ~0.60 px per char.
      const textWDots = textW * 203 / 25.4;
      const tavoloPt = Math.max(9, Math.min(16,
        Math.floor((textWDots / tavoloText.length) / (203 / 72 * 0.60) * 0.92),
      ));
      const tavoloH = Math.ceil((tavoloPt * 203 / 72 * 1.2 * 25.4 / 203) * 10) / 10 + 0.4;

      // Auto-scale nome locale (una riga sola, no split).
      const namePt = Math.max(7, Math.min(11,
        Math.floor((textWDots / Math.max(businessName.length, 1)) / (203 / 72 * 0.60) * 0.92),
      ));
      const nameH = Math.ceil((namePt * 203 / 72 * 1.2 * 25.4 / 203) * 10) / 10 + 0.3;

      // Àncora più piccola del layout senza-tavolo (10mm@scale=1 vs 13mm).
      const ancH = 10.0 * scale;
      const gap = 0.6 * scale;

      const totalBlockH = tavoloH + gap + ancH + gap + nameH;
      const topPad = Math.max(0.3, (H - totalBlockH) / 2);
      let curY = topPad;

      elements.push({
        id: 'tavolo_label', type: 'text',
        x_mm: textX, y_mm: curY,
        width_mm: textW, height_mm: tavoloH,
        font_size_pt: tavoloPt, font_weight: 'bold',
        align: 'center',
        static_value: tavoloText, rotation: 0,
      });
      curY += tavoloH + gap;

      elements.push({
        id: 'anchor', type: 'text',
        x_mm: textX, y_mm: curY,
        width_mm: textW, height_mm: ancH,
        font_size_pt: 1, // ignorato (marker @anchor)
        static_value: '@anchor', rotation: 0,
      });
      curY += ancH + gap;

      elements.push({
        id: 'biz_name', type: 'text',
        x_mm: textX, y_mm: curY,
        width_mm: textW, height_mm: nameH,
        font_size_pt: namePt, font_weight: 'bold',
        align: 'center',
        static_value: businessName, rotation: 0,
      });

      void location; void tagline;
    } else {
      // ── Layout SENZA tavolo (QR generale del menu) ──────────────────
      // Nome diviso in 2 righe: prima parte (prima della preposizione) sopra,
      // resto sotto. Es. "La Terrazza di Giò" → "La Terrazza" / "di Giò".
      const m = /^(.*?)\s+(di|del|della|dei|degli|delle|al|alla|a|d')\s+(.+)$/i.exec(businessName.trim());
      const nameTop = m ? m[1] : businessName;
      const nameBottom = m ? `${m[2]} ${m[3]}` : '';

      // Auto-scaling del font del nome (vedi commit history per dettagli).
      const textWDots = textW * 203 / 25.4;
      const maxChars = Math.max(nameTop.length, nameBottom?.length ?? 1);
      const ptFromWidth = (textWDots / maxChars) / (203 / 72 * 0.60) * 0.92;
      const FONT_NAME = Math.max(8, Math.min(14, Math.floor(ptFromWidth)));

      const lineHmm = (FONT_NAME * 203 / 72) * 1.2 * 25.4 / 203;
      const NAME_H = Math.ceil(lineHmm * 10) / 10 + 0.5;

      const totalBlockH = (13.0 + 1.0 + NAME_H + (nameBottom ? NAME_H : 0)) * scale;
      const topPad = Math.max(0.4, (H - totalBlockH) / 2);
      const ANC_H = 13.0 * scale;
      let curY = topPad;
      elements.push({
        id: 'anchor', type: 'text',
        x_mm: textX, y_mm: curY,
        width_mm: textW, height_mm: ANC_H,
        font_size_pt: 1,
        static_value: '@anchor', rotation: 0,
      });
      curY += ANC_H + 0.6 * scale;

      elements.push({
        id: 'name_top', type: 'text',
        x_mm: textX, y_mm: curY,
        width_mm: textW, height_mm: NAME_H * scale,
        font_size_pt: FONT_NAME, font_weight: 'bold',
        static_value: nameTop, rotation: 0,
      });
      curY += NAME_H * scale;

      if (nameBottom) {
        elements.push({
          id: 'name_bottom', type: 'text',
          x_mm: textX, y_mm: curY,
          width_mm: textW, height_mm: NAME_H * scale,
          font_size_pt: FONT_NAME, font_weight: 'bold',
          static_value: nameBottom, rotation: 0,
        });
      }

      void location; void tagline;
    }
  } else {
    // Square / Portrait: QR in alto centrato, testi sotto.
    const reserveTextH = Math.min(H * 0.30, 12); // ~30% per i testi
    const qrSize = Math.max(10, Math.min(W - 2 * margin, H - reserveTextH - 2 * margin));
    const qrX = (W - qrSize) / 2;
    const qrY = margin;
    elements.push({
      id: 'qr',
      type: 'qr',
      x_mm: qrX,
      y_mm: qrY,
      width_mm: qrSize,
      height_mm: qrSize,
      static_value: url,
      rotation: 0,
    });
    let y = qrY + qrSize + 1.2;
    elements.push({
      id: 'biz_name',
      type: 'text',
      x_mm: margin,
      y_mm: y,
      width_mm: W - 2 * margin,
      font_size_pt: 9,
      font_weight: 'bold',
      static_value: businessName,
      rotation: 0,
    });
    y += 4;
    elements.push({
      id: 'cta',
      type: 'text',
      x_mm: margin,
      y_mm: y,
      width_mm: W - 2 * margin,
      font_size_pt: 6,
      static_value: tableLabel ? `Tavolo ${tableLabel} · Inquadra il QR per il menu` : 'Inquadra il QR per il menu',
      rotation: 0,
    });
  }

  return {
    id: '00000000-0000-0000-0000-000000000001',
    tenant_id: '00000000-0000-0000-0000-000000000000',
    business_id: null,
    name: tableLabel ? `menu_qr_table_${tableLabel}` : 'menu_qr',
    preset: 'takeaway',
    width_mm: W,
    height_mm: H,
    required_allergens: false,
    created_at: new Date().toISOString(),
    archived_at: null,
    version: 0,
    elements,
  };
}

function randomToken(len = 16): string {
  const arr = new Uint8Array(len);
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < arr.length; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  return [...arr].map((b) => b.toString(16).padStart(2, '0')).join('');
}

const ALLERGEN_VALUES = AllergenSchema.options;

const PUBLIC_BASE = 'https://trackeat.eu';

// T-076: teaser promozionale per il servizio Menu pubblico. Mostrato ai
// tenant che non hanno (ancora) il servizio Menu attivato dal consulente.
// Stesso pattern + look-and-feel di `ManualeTeaser` per coerenza.
function MenuTeaser(): JSX.Element {
  const subject = encodeURIComponent('Richiesta menu digitale per il mio locale');
  const body = encodeURIComponent(
    [
      'Ciao Pierpaolo,',
      '',
      'Sono interessato/a al servizio di menu pubblico digitale su TrackEAT.',
      '',
      'Puoi richiamarmi per spiegarmi come funziona e i tempi?',
      '',
      'Grazie!',
    ].join('\n'),
  );
  const mailto = `mailto:criscenzo.pierpaolo@gmail.com?subject=${subject}&body=${body}`;
  const whatsappUrl = 'https://wa.me/393486382343?text=' + encodeURIComponent(
    'Ciao Pierpaolo, sono interessato/a al menu digitale TrackEAT. Mi puoi spiegare come funziona?',
  );

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/today" />
          </IonButtons>
          <IonTitle>Menù digitale</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: 16 }}>
          {/* Hero — stesso treatment grafico di ManualeTeaser per coerenza
              visiva tra le card promozionali. */}
          <div
            style={{
              background: 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)',
              color: '#fff',
              borderRadius: 16,
              padding: '22px 20px',
              marginBottom: 20,
              boxShadow: '0 4px 16px rgba(225, 29, 72, 0.25)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <IonIcon icon={ribbonOutline} style={{ fontSize: 20 }} />
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.92 }}>
                Servizio TrackEAT Premium
              </span>
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 8px', lineHeight: 1.2 }}>
              Il tuo menù online, con un QR
            </h1>
            <p style={{ fontSize: 14, margin: 0, opacity: 0.95, lineHeight: 1.5 }}>
              Niente PDF stampati che si scollano dal tavolo. Il consulente
              monta il tuo menù — piatti, foto, allergeni, prezzi — con la
              grafica del tuo locale. Il cliente lo apre con il QR del tavolo,
              tu lo aggiorni in tempo reale.
            </p>
          </div>

          {/* Benefit list */}
          <h2 style={{ fontSize: 13, fontWeight: 700, color: '#475569', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '0 0 12px' }}>
            Perché averlo
          </h2>
          <div style={{ display: 'grid', gap: 10, marginBottom: 24 }}>
            {[
              {
                icon: colorPaletteOutline,
                title: 'Identità del tuo locale',
                body: 'Colori, font, mood: ogni menù ha la sua personalità — mai un template anonimo. Si riconosce subito che è il tuo.',
              },
              {
                icon: phonePortraitOutline,
                title: 'Niente app da scaricare',
                body: 'Il cliente apre il QR sul telefono e vede il menù in 1 secondo. Funziona anche per chi non sa usare lo smartphone bene.',
              },
              {
                icon: sparklesOutline,
                title: 'Allergeni a norma',
                body: 'Reg. UE 1169/2011 sempre rispettato: ogni piatto ha gli allergeni evidenziati, badge vegetariano/vegano dove serve.',
              },
              {
                icon: storefrontOutline,
                title: 'Cambi prezzo in 2 minuti',
                body: 'Hai aumentato il vino di 0,50€? Lo modifichi qui in app, il cliente alla scansione successiva lo vede già aggiornato.',
              },
              {
                icon: qrCodeOutline,
                title: 'QR per il tavolo + per la vetrina',
                body: 'Genera il QR del menù pronto da stampare e attaccare: tavoli, porta, asporto. Foto dei piatti incluse.',
              },
            ].map((b) => (
              <div
                key={b.title}
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  padding: 14,
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: '#fee2e2',
                    color: '#e11d48',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IonIcon icon={b.icon} style={{ fontSize: 20 }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 2 }}>{b.title}</div>
                  <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.4 }}>{b.body}</div>
                </div>
              </div>
            ))}
          </div>

          {/* How it works */}
          <h2 style={{ fontSize: 13, fontWeight: 700, color: '#475569', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '0 0 12px' }}>
            Come funziona
          </h2>
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: 16,
              marginBottom: 24,
            }}
          >
            {[
              { n: 1, title: 'Mandami il menù che hai oggi', body: 'PDF, foto del cartaceo, anche un messaggio vocale. Mi basta quello — io lo digito + categorizzo per te.' },
              { n: 2, title: 'Setup grafico del tuo locale', body: 'Scegliamo insieme colori, font e mood — il menù sarà coerente con la tua identità, non un template generico.' },
              { n: 3, title: 'Allergeni + foto', body: 'Compilo gli allergeni Reg. UE 1169/2011 e (se vuoi) le foto dei piatti. Vegetariano/vegano evidenziato.' },
              { n: 4, title: 'QR pronto da stampare', body: 'Ti consegno il QR per i tavoli + per la vetrina. Da quel momento, ogni cambio prezzo lo fai tu in 30 secondi qui in app.' },
            ].map((s, i, arr) => (
              <div
                key={s.n}
                style={{
                  display: 'flex',
                  gap: 14,
                  paddingBottom: i === arr.length - 1 ? 0 : 14,
                  borderBottom: i === arr.length - 1 ? 'none' : '1px solid #f1f5f9',
                  marginBottom: i === arr.length - 1 ? 0 : 14,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    background: '#e11d48',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {s.n}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 2 }}>{s.title}</div>
                  <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.4 }}>{s.body}</div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <h2 style={{ fontSize: 13, fontWeight: 700, color: '#475569', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '0 0 12px' }}>
            Vuoi vedere come sarebbe?
          </h2>
          <div style={{ display: 'grid', gap: 10, marginBottom: 24 }}>
            {/* T-076: Prenota call — riusa il booking widget del sito web. */}
            <button
              type="button"
              onClick={() => { void openBookingPage(); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                background: '#0066cc',
                color: '#fff',
                padding: '14px 16px',
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 15,
                border: 0,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0, 102, 204, 0.3)',
              }}
            >
              <IonIcon icon={calendarOutline} style={{ fontSize: 22 }} />
              Prenota una call
            </button>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                background: '#25d366',
                color: '#fff',
                padding: '14px 16px',
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 15,
                textDecoration: 'none',
                boxShadow: '0 2px 8px rgba(37, 211, 102, 0.3)',
              }}
            >
              <IonIcon icon={logoWhatsapp} style={{ fontSize: 22 }} />
              Scrivimi su WhatsApp
            </a>
            <a
              href={mailto}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                background: '#fff',
                color: '#0066cc',
                border: '1.5px solid #0066cc',
                padding: '14px 16px',
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 15,
                textDecoration: 'none',
              }}
            >
              <IonIcon icon={mailOutline} style={{ fontSize: 20 }} />
              Manda una email
            </a>
          </div>

          {/* Reassurance footer */}
          <div
            style={{
              padding: '12px 14px',
              background: '#fff1f2',
              border: '1px solid #fecdd3',
              borderRadius: 10,
              fontSize: 12,
              color: '#9f1239',
              lineHeight: 1.5,
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
            }}
          >
            <IonIcon icon={checkmarkCircleOutline} style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }} />
            <IonText>
              <strong>Nessun impegno.</strong> Ti faccio vedere un menù demo
              fatto per un altro locale (con la sua identità grafica), poi
              decidi tu. Se vuoi fare prima qualche modifica, parliamone insieme.
            </IonText>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}

// T-076: dispatcher top-level. Tenant senza `is_platform_owner` (= ristoratore
// cliente che non ha ancora il servizio Menu setup dal consulente) vede il
// teaser promozionale. Il consulente (platform owner, anche quando impersona
// un tenant) vede sempre la vista gestione attuale — NESSUNA modifica.
export function MenuPage(): JSX.Element {
  const { profile } = useAuth();
  return <MenuOwnerView qrOnly={!profile?.is_platform_owner} />;
}

function MenuOwnerView({ qrOnly = false }: { qrOnly?: boolean }): JSX.Element {
  const location = useLocation();
  const menuType = menuTypeFromSearch(location.search);
  const setMenuType = (type: MenuType): void => history.replace(`${location.pathname}?view=${type}`);
  const [reload, setReload] = useState(0);
  useIonViewWillEnter(() => setReload((n) => n + 1));
  const { profile } = useAuth();
  const sb = getSupabase();
  const celebrate = useCelebrate();
  const history = useHistory();
  const canEdit = profile?.is_platform_owner === true || canEditMenu(profile?.role ?? null);

  const [business, setBusiness] = useState<BusinessRow | null>(null);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [items, setItems] = useState<ItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  const [showQr, setShowQr] = useState(false);
  // T-053: target del QR mostrato — 'menu' (link al menù completo) o
  // 'aperitivo' (link che apre direttamente la scheda Aperitivo, per
  // stampare banner al bancone). Il publicUrl cambia di conseguenza.
  const [qrTarget, setQrTarget] = useState<MenuType | 'aperitivo'>(menuType);
  useEffect(() => setQrTarget(menuType), [menuType]);
  const targetLabel = (target: MenuType | 'aperitivo'): string => target === 'takeaway' ? 'Menù asporto' : target === 'aperitivo' ? 'Aperitivo' : 'Menù sala';
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const qrCanvas = useRef<HTMLCanvasElement | null>(null);

  // T-702: la stampante è una periferica del dispositivo, non del locale.
  const {
    printers: devicePrinters,
    defaultId: deviceDefaultPrinterId,
    loaded: devicePrintersLoaded,
  } = useDevicePrinters();
  const printablePrinters = useMemo(
    () => devicePrinters.filter((p) => (
      p.driver !== 'preview'
      && ((p.driver !== 'system' && p.driver !== 'qz') || !Capacitor.isNativePlatform())
    )),
    [devicePrinters],
  );
  const [showPrint, setShowPrint] = useState(false);
  const [printPrinterId, setPrintPrinterId] = useState<string | null>(null);
  const [printCopies, setPrintCopies] = useState(1);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!devicePrintersLoaded) return;
    if (printPrinterId && printablePrinters.some((p) => p.id === printPrinterId)) return;
    const resolved = resolveDefaultPrinterId(
      printablePrinters,
      deviceDefaultPrinterId,
      PREVIEW_PRINTER_ID,
    );
    setPrintPrinterId(resolved === PREVIEW_PRINTER_ID ? null : resolved);
  }, [deviceDefaultPrinterId, devicePrintersLoaded, printablePrinters, printPrinterId]);

  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatIcon, setNewCatIcon] = useState('🍽️');
  const [savingCat, setSavingCat] = useState(false);

  const [addItemCatId, setAddItemCatId] = useState<string | null>(null);
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    price: '',
    station: 'cucina' as 'cucina' | 'bar',
    allergens: [] as string[],
  });
  const [savingItem, setSavingItem] = useState(false);
  useEffect(() => { setShowAddCat(false); setAddItemCatId(null); }, [menuType]);

  const businessId = profile?.active_business_id ?? null;
  const tenantId = profile?.active_tenant_id ?? null;

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!businessId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const [bRes, cRes, iRes] = await Promise.all([
          sb.from('businesses').select('id,name,menu_public_slug,menu_published,menu_theme').eq('id', businessId).single(),
          sb.from(menuTables(menuType).categories).select('*').eq('business_id', businessId).order('sort_order', { ascending: true }),
          sb.from(menuTables(menuType).items).select('*').eq('business_id', businessId).order('sort_order', { ascending: true }),
        ]);
        if (cancelled) return;
        if (bRes.error) throw bRes.error;
        if (cRes.error) throw cRes.error;
        if (iRes.error) throw iRes.error;
        setBusiness(bRes.data as BusinessRow);
        setCategories((cRes.data ?? []) as CategoryRow[]);
        setItems((iRes.data ?? []) as ItemRow[]);
      } catch (err) {
        if (!cancelled) setToast(toUserMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [businessId, sb, menuType, reload]);

  const publicUrl = useMemo(() => {
    if (!business?.menu_public_slug) return null;
    return `${PUBLIC_BASE}/menu/${business.menu_public_slug}`;
  }, [business?.menu_public_slug]);

  // T-053: URL effettivo del QR in base alla scheda scelta dall'utente.
  // ?view=aperitivo è gestito dalla pagina pubblica (T-055): apre il
  // menu direttamente sulla scheda Aperitivo.
  const qrUrl = useMemo(() => {
    if (!publicUrl) return null;
    return menuViewUrl(publicUrl, qrTarget);
  }, [publicUrl, qrTarget]);

  useEffect(() => {
    if (!qrUrl) return;
    let cancelled = false;
    setQrDataUrl(null); // reset durante la rigenerazione → mostra spinner
    void (QRCode.toDataURL(qrUrl, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 480,
      color: { dark: '#0f172a', light: '#ffffff' },
    }) as Promise<string>)
      .then((url: string) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setToast('Impossibile generare il QR');
      });
    return () => {
      cancelled = true;
    };
  }, [qrUrl]);

  // T-056: condividi il QR via sheet di sistema (WhatsApp/Mail/...).
  // Su Capacitor native (Android/iOS) Web Share API è inaffidabile,
  // quindi: (a) scrivo il PNG sul filesystem temp via @capacitor/filesystem,
  // (b) chiamo @capacitor/share con il path del file. Su PWA/browser
  // fallback su Web Share API con File, e ultimo fallback download.
  async function shareQr(): Promise<void> {
    if (!qrDataUrl || !qrUrl) return;
    const qrLabel = targetLabel(qrTarget);
    const filename = `qr-${qrTarget}.png`;
    const shareTitle = `${qrLabel} di ${business?.name ?? ''}`.trim();
    const shareText = `${shareTitle} — ${qrUrl}`;
    try {
      if (Capacitor.isNativePlatform()) {
        // base64 puro (rimuovi prefisso data:image/png;base64,)
        const base64 = qrDataUrl.replace(/^data:image\/png;base64,/, '');
        const writeResult = await Filesystem.writeFile({
          path: filename,
          data: base64,
          directory: Directory.Cache,
        });
        await Share.share({
          title: shareTitle,
          text: shareText,
          url: writeResult.uri,
          dialogTitle: 'Condividi QR',
        });
        return;
      }
      // Web/PWA: Web Share API con file
      const blob = await (await fetch(qrDataUrl)).blob();
      const file = new File([blob], filename, { type: 'image/png' });
      const navAny = navigator as Navigator & {
        canShare?: (data: ShareData) => boolean;
        share?: (data: ShareData) => Promise<void>;
      };
      const dataWithFile: ShareData = { title: shareTitle, text: shareText, files: [file] };
      if (typeof navAny.canShare === 'function' && navAny.canShare(dataWithFile) && navAny.share) {
        await navAny.share(dataWithFile);
        return;
      }
      if (typeof navAny.share === 'function') {
        await navAny.share({ title: shareTitle, text: shareText, url: qrUrl });
        return;
      }
      // Ultimo fallback (no Web Share API): download del PNG
      const a = document.createElement('a');
      a.href = qrDataUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setToast('QR scaricato — usa il menu condividi del file');
    } catch (err) {
      // Cancellazione utente → silenzioso
      const errName = (err as { name?: string } | null)?.name;
      if (errName === 'AbortError') return;
      // Il plugin Capacitor Share lancia con message "Share canceled" se l'utente
      // chiude lo sheet — anche questo non è un vero errore.
      const msg = (err as { message?: string } | null)?.message ?? '';
      if (/canceled|cancelled/i.test(msg)) return;
      setToast(toUserMessage(err));
    }
  }

  async function downloadQr(): Promise<void> {
    if (!qrDataUrl || !qrUrl) return;
    if (Capacitor.isNativePlatform()) {
      await shareQr();
      return;
    }
    const filename = `qr-${qrTarget}.png`;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    celebrate('QR scaricato');
  }

  // T-057: genera un PDF vero del menù lato app (jsPDF) e lo condivide
  // come file via @capacitor/share. Niente link, niente browser di
  // sistema — l'utente riceve direttamente il file PDF nel sheet di
  // condivisione e lo gira su WhatsApp/Mail/Drive.
  async function exportMenuPdf(target: MenuType | 'aperitivo'): Promise<void> {
    if (!business) return;
    const label = targetLabel(target);
    const filename = `menu-${target}.pdf`;
    try {
      setToast(`Genero PDF ${label.toLowerCase()}…`);
      const pdf = generateMenuPdf({
        businessName: business.name,
        tagline: business.menu_theme?.tagline ?? null,
        chefName: business.menu_theme?.chef_name ?? null,
        chefSubtitle: business.menu_theme?.chef_subtitle ?? null,
        location: business.menu_theme?.location ?? null,
        primaryColor: business.menu_theme?.palette?.primary ?? null,
        accentColor: business.menu_theme?.palette?.accent ?? null,
        categories,
        items,
        target,
        allergenLabels: ALLERGEN_LABELS_IT,
      });
      const shareTitle = `${label} di ${business.name}`;
      const shareText = `Ecco il nostro ${label.toLowerCase()} in PDF`;

      if (Capacitor.isNativePlatform()) {
        // Scrivi il PDF su Cache filesystem, poi share del path
        const writeResult = await Filesystem.writeFile({
          path: filename,
          data: pdf.base64,
          directory: Directory.Cache,
        });
        await Share.share({
          title: shareTitle,
          text: shareText,
          url: writeResult.uri,
          dialogTitle: `Condividi ${label}`,
        });
        setToast('');
        return;
      }

      // Web/PWA: download del PDF
      const blob = new Blob([pdf.arrayBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setToast('PDF scaricato');
    } catch (err) {
      const errName = (err as { name?: string } | null)?.name;
      if (errName === 'AbortError') return;
      const msg = (err as { message?: string } | null)?.message ?? '';
      if (/canceled|cancelled/i.test(msg)) return;
      setToast(toUserMessage(err));
    }
  }

  async function togglePublish(next: boolean): Promise<void> {
    if (!businessId) return;
    try {
      const { error } = await sb.from('businesses').update({ menu_published: next }).eq('id', businessId);
      if (error) throw error;
      setBusiness((b) => (b ? { ...b, menu_published: next } : b));
      celebrate(next ? 'Menu pubblicato' : 'Menu nascosto');
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function createCategory(): Promise<void> {
    const name = newCatName.trim();
    if (!name || !businessId || !tenantId) {
      setToast('Nome categoria richiesto');
      return;
    }
    setSavingCat(true);
    try {
      const nextOrder = (categories[categories.length - 1]?.sort_order ?? -10) + 10;
      const { data, error } = await sb
        .from(menuTables(menuType).categories)
        .insert({
          tenant_id: tenantId,
          business_id: businessId,
          name,
          icon: newCatIcon || null,
          sort_order: nextOrder,
          visible: true,
        })
        .select('*')
        .single();
      if (error || !data) throw error ?? new Error('insert failed');
      setCategories((prev) => [...prev, data as CategoryRow]);
      setNewCatName('');
      setNewCatIcon('🍽️');
      setShowAddCat(false);
      celebrate(`Categoria "${name}" creata`);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setSavingCat(false);
    }
  }

  async function createItem(): Promise<void> {
    if (!addItemCatId || !businessId || !tenantId) return;
    const name = newItem.name.trim();
    if (!name) {
      setToast('Nome piatto richiesto');
      return;
    }
    const priceNum = Number(newItem.price.replace(',', '.'));
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      setToast('Prezzo non valido');
      return;
    }
    setSavingItem(true);
    try {
      const cur = items.filter((i) => i.category_id === addItemCatId);
      const nextOrder = (cur[cur.length - 1]?.sort_order ?? -10) + 10;
      const { data, error } = await sb
        .from(menuTables(menuType).items)
        .insert({
          tenant_id: tenantId,
          business_id: businessId,
          category_id: addItemCatId,
          name,
          description: newItem.description.trim() || null,
          price_cents: Math.round(priceNum * 100),
          allergens: newItem.allergens,
          station: newItem.station,
          sort_order: nextOrder,
          visible: true,
        })
        .select('*')
        .single();
      if (error || !data) throw error ?? new Error('insert failed');
      setItems((prev) => [...prev, data as ItemRow]);
      celebrate(`"${name}" aggiunto al menu`);
      setNewItem({ name: '', description: '', price: '', station: 'cucina', allergens: [] });
      setAddItemCatId(null);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setSavingItem(false);
    }
  }

  function copyUrl(): void {
    if (!qrUrl) return;
    try {
      void navigator.clipboard.writeText(qrUrl);
      setToast('Link copiato');
    } catch {
      setToast(qrUrl);
    }
  }

  // T-026: stampa QR personalizzato (nome locale + tagline a fianco). Per
  // un tavolo specifico passare `tableLabel` + URL con `?t=<token>`.
  async function printQrLabel(args?: { tableLabel?: string; urlOverride?: string }): Promise<void> {
    const targetUrl = args?.urlOverride ?? qrUrl;
    if (!targetUrl) return;
    const printer = printablePrinters.find((p) => p.id === printPrinterId);
    if (!printer) {
      setToast('Nessuna stampante selezionata');
      return;
    }
    const copies = Math.max(1, Math.min(99, Math.round(printCopies)));
    setPrinting(true);
    try {
      const template = buildQrLabelTemplate({
        url: targetUrl,
        sizeMm: printer.label_size_mm,
        businessName: business?.name ?? 'Menu',
        tagline: business?.menu_theme?.tagline,
        location: business?.menu_theme?.location,
        tableLabel: args?.tableLabel,
      });
      const adapter = createPrinterAdapter(printer.driver);
      const session = await adapter.connect({
        id: printer.id,
        name: printer.name,
        driver: printer.driver,
        connection: printer.connection,
        address: printer.address,
      });
      await adapter.printLabel(session, template, { menu_url: targetUrl } as Record<string, unknown>, copies);
      setShowPrint(false);
      celebrate(args?.tableLabel ? `Stampato QR · Tavolo ${args.tableLabel}` : `Stampa avviata · ${copies}× QR`);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setPrinting(false);
    }
  }

  // ── Tavoli (T-026 follow-up, T-021 prep, T-028 restyle) ─────────────────
  const [tables, setTables] = useState<TableRow[]>([]);
  const [loadingTables, setLoadingTables] = useState(false);
  // T-028: form ridotto a "quanti tavoli creare?". Default 1 per non
  // creare batch enormi per errore.
  const [bulkCount, setBulkCount] = useState<number>(1);
  const [savingTable, setSavingTable] = useState(false);
  // T-028: double-check stampa di tutti i QR.
  const [showPrintAllConfirm, setShowPrintAllConfirm] = useState(false);
  const [printingAll, setPrintingAll] = useState(false);
  const [printAllProgress, setPrintAllProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });

  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    void (async () => {
      setLoadingTables(true);
      const { data, error } = await sb
        .from('restaurant_tables')
        .select('id,label,qr_token,sort_order,active')
        .eq('business_id', businessId)
        .eq('active', true)
        .order('sort_order', { ascending: true });
      if (cancelled) return;
      setLoadingTables(false);
      if (error) { setToast(toUserMessage(error)); return; }
      setTables((data ?? []) as TableRow[]);
    })();
    return () => { cancelled = true; };
  }, [businessId, sb]);

  function tableUrl(token: string): string {
    return publicUrl ? `${publicUrl}?t=${encodeURIComponent(token)}` : '';
  }

  // T-028: crea N tavoli numerici sequenziali (1, 2, 3, ...). Il primo
  // numero è max(label_numerico_esistente)+1. Se ci sono già etichette
  // non numeriche (es. "Terrazza"), vengono ignorate per il calcolo del
  // prossimo numero. Insert in batch singolo.
  async function addTablesBulk(): Promise<void> {
    const n = Math.max(1, Math.min(50, Math.round(bulkCount)));
    if (!businessId || !tenantId) {
      setToast('Nessun locale attivo');
      return;
    }
    setSavingTable(true);
    try {
      const numericLabels = tables
        .map((t) => Number(t.label))
        .filter((v) => Number.isFinite(v) && v > 0);
      const startFrom = numericLabels.length > 0 ? Math.max(...numericLabels) + 1 : 1;
      const baseOrder = tables.length > 0 ? (tables[tables.length - 1].sort_order ?? 0) + 10 : 10;

      const rows = Array.from({ length: n }, (_, i) => ({
        tenant_id: tenantId,
        business_id: businessId,
        label: String(startFrom + i),
        qr_token: randomToken(8),
        sort_order: baseOrder + i * 10,
        active: true,
      }));

      const { data, error } = await sb
        .from('restaurant_tables')
        .insert(rows)
        .select('id,label,qr_token,sort_order,active');
      if (error || !data) throw error ?? new Error('insert failed');
      setTables((prev) => [...prev, ...(data as TableRow[])]);
      setBulkCount(1);
      celebrate(
        n === 1
          ? `Tavolo ${startFrom} creato`
          : `Creati ${n} tavoli (${startFrom}–${startFrom + n - 1})`,
      );
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setSavingTable(false);
    }
  }

  async function printTableQr(t: TableRow): Promise<void> {
    if (printablePrinters.length === 0) {
      setToast('Nessuna stampante configurata. Vai in Impostazioni → Stampante.');
      return;
    }
    if (!printPrinterId) {
      setPrintPrinterId(printablePrinters[0].id);
    }
    await printQrLabel({ tableLabel: t.label, urlOverride: tableUrl(t.qr_token) });
  }

  // T-028: stampa tutti i QR dei tavoli in sequenza, gated da modale di
  // conferma. Una etichetta per tavolo, in ordine `sort_order`. Se la
  // stampa di un tavolo fallisce, ci si ferma e si segnala quale tavolo
  // ha bloccato la sequenza (così l'utente non si ritrova con metà
  // rotolo bruciato senza sapere a che punto è arrivato).
  async function printAllTableQrs(): Promise<void> {
    if (tables.length === 0) return;
    if (printablePrinters.length === 0) {
      setToast('Nessuna stampante configurata. Vai in Impostazioni → Stampante.');
      return;
    }
    const printer = printablePrinters.find((p) => p.id === (printPrinterId || printablePrinters[0].id));
    if (!printer) {
      setToast('Nessuna stampante selezionata');
      return;
    }
    setShowPrintAllConfirm(false);
    setPrintingAll(true);
    setPrintAllProgress({ done: 0, total: tables.length });
    try {
      const adapter = createPrinterAdapter(printer.driver);
      const session = await adapter.connect({
        id: printer.id,
        name: printer.name,
        driver: printer.driver,
        connection: printer.connection,
        address: printer.address,
      });
      let i = 0;
      for (const t of tables) {
        const targetUrl = tableUrl(t.qr_token);
        if (!targetUrl) continue;
        const template = buildQrLabelTemplate({
          url: targetUrl,
          sizeMm: printer.label_size_mm,
          businessName: business?.name ?? 'Menu',
          tagline: business?.menu_theme?.tagline,
          location: business?.menu_theme?.location,
          tableLabel: t.label,
        });
        try {
          await adapter.printLabel(session, template, { menu_url: targetUrl } as Record<string, unknown>, 1);
        } catch (err) {
          throw new Error(`Stampa interrotta al tavolo ${t.label}: ${toUserMessage(err)}`);
        }
        i += 1;
        setPrintAllProgress({ done: i, total: tables.length });
      }
      celebrate(`Stampati ${i} QR tavolo`);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setPrintingAll(false);
    }
  }

  if (qrOnly && !loading && !hasPublishedCustomMenu(business)) {
    return <MenuTeaser />;
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/settings" />
          </IonButtons>
          <IonTitle>Menù del locale</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <IonSpinner name="dots" />
          </div>
        ) : !business ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>
            Nessun locale attivo.
          </div>
        ) : (
          <>
            {business.menu_public_slug === 'lo-sperdicchio' && (
              <IonSegment value={menuType} onIonChange={(e) => setMenuType(e.detail.value === 'takeaway' ? 'takeaway' : 'menu')} aria-label="Tipo di menù" style={{ margin: '12px 16px', width: 'auto' }}>
                <IonSegmentButton value="menu"><IonLabel>Menù sala</IonLabel></IonSegmentButton>
                <IonSegmentButton value="takeaway"><IonLabel>Menù asporto</IonLabel></IonSegmentButton>
              </IonSegment>
            )}
            {/* Card hero: business + slug + QR */}
            <div style={{ padding: '12px 16px 4px' }}>
              <div
                style={{
                  background: 'linear-gradient(135deg,#0066cc,#8b5cf6)',
                  color: '#fff',
                  borderRadius: 18,
                  padding: '18px 18px',
                  boxShadow: '0 6px 20px rgba(102,102,204,.35)',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.88, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  📖 Menù pubblico
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4 }}>
                  {business.name}
                </div>
                {publicUrl ? (
                  <div
                    style={{
                      marginTop: 8,
                      background: 'rgba(255,255,255,.18)',
                      borderRadius: 10,
                      padding: '8px 12px',
                      fontSize: 12,
                      wordBreak: 'break-all',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                  >
                    {qrUrl}
                  </div>
                ) : (
                  <div style={{ marginTop: 8, fontSize: 12, opacity: 0.88 }}>
                    Nessuno slug pubblico configurato per questo locale.
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => { if (qrOnly) void downloadQr(); else setShowQr(true); }}
                    disabled={!publicUrl || (qrOnly && !qrDataUrl)}
                    style={{
                      flex: 1, minWidth: 110,
                      background: '#fff', color: '#0f172a',
                      border: 'none', borderRadius: 10, padding: '10px 12px',
                      minHeight: 44,
                      fontWeight: 700, fontSize: 13,
                      cursor: publicUrl && (!qrOnly || qrDataUrl) ? 'pointer' : 'not-allowed',
                      opacity: publicUrl && (!qrOnly || qrDataUrl) ? 1 : 0.6,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    <IonIcon icon={qrOnly ? downloadOutline : phonePortraitOutline} style={{ fontSize: 18 }} />
                    {qrOnly ? 'Scarica QR' : 'Mostra QR'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPrint(true)}
                    disabled={!publicUrl}
                    style={{
                      flex: 1, minWidth: 110,
                      background: '#fff', color: '#0f172a',
                      border: 'none', borderRadius: 10, padding: '10px 12px',
                      minHeight: 44,
                      fontWeight: 700, fontSize: 13, cursor: publicUrl ? 'pointer' : 'not-allowed',
                      opacity: publicUrl ? 1 : 0.6,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    <IonIcon icon={printOutline} style={{ fontSize: 18 }} />
                    Stampa QR
                  </button>
                  <button
                    type="button"
                    onClick={copyUrl}
                    disabled={!publicUrl}
                    style={{
                      flex: 1, minWidth: 110,
                      background: 'rgba(255,255,255,.18)', color: '#fff',
                      border: '1px solid rgba(255,255,255,.4)', borderRadius: 10,
                      padding: '10px 12px', fontWeight: 700, fontSize: 13,
                      minHeight: 44,
                      cursor: publicUrl ? 'pointer' : 'not-allowed',
                      opacity: publicUrl ? 1 : 0.6,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    <IonIcon icon={linkOutline} style={{ fontSize: 18 }} />
                    Copia link
                  </button>
                </div>
                {!qrOnly && (<>
                {/* T-056: condividi il link del menù via sheet di sistema
                    (WhatsApp, Mail, ecc.). Sostituisce il vecchio "Esporta PDF"
                    che apriva window.print() — su Capacitor WebView non
                    funziona affidabile e l'utente voleva proprio uno share. */}
                <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => { void exportMenuPdf(menuType); }}
                    disabled={!publicUrl}
                    style={{
                      flex: 1, minWidth: 130,
                      background: 'rgba(255,255,255,.10)', color: '#fff',
                      border: '1px solid rgba(255,255,255,.30)', borderRadius: 10,
                      padding: '10px 12px', fontWeight: 700, fontSize: 13,
                      cursor: publicUrl ? 'pointer' : 'not-allowed',
                      opacity: publicUrl ? 1 : 0.6,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    <span style={{ fontSize: 15 }}>📄</span>
                    Esporta PDF {menuType === 'takeaway' ? 'asporto' : 'sala'}
                  </button>
                  {menuType === 'menu' && <button
                    type="button"
                    onClick={() => { void exportMenuPdf('aperitivo'); }}
                    disabled={!publicUrl}
                    style={{
                      flex: 1, minWidth: 130,
                      background: 'rgba(255,255,255,.10)', color: '#fff',
                      border: '1px solid rgba(255,255,255,.30)', borderRadius: 10,
                      padding: '10px 12px', fontWeight: 700, fontSize: 13,
                      cursor: publicUrl ? 'pointer' : 'not-allowed',
                      opacity: publicUrl ? 1 : 0.6,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    <span style={{ fontSize: 15 }}>📄</span>
                    Esporta PDF Aperitivo
                  </button>}
                </div>
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontSize: 12 }}>
                    Visibile al pubblico: <b>{business.menu_published ? 'sì' : 'no'}</b>
                  </span>
                  <IonToggle
                    checked={business.menu_published}
                    onIonChange={(ev) => { void togglePublish(!!ev.detail.checked); }}
                  />
                </div>
                </>)}
              </div>
            </div>

            {!qrOnly && (<>
            {/* T-068: Cassetto menu del cliente — entry point per
                la pagina dedicata di upload foto/PDF dei menu reali del
                cliente. L'agente LLM li elabora su richiesta in chat per
                fare il First Setup degli alimenti / ricreare il menù
                pubblico.
                Visibile SOLO al platform owner (consulente HACCP). Il
                cliente non vede né questa card né la pagina linkata. */}
            {profile?.is_platform_owner && (
              <div style={{ padding: '8px 16px 0' }}>
                <button
                  type="button"
                  onClick={() => history.push('/settings/menu/uploads')}
                  style={{
                    width: '100%',
                    background: '#fff',
                    border: '1px dashed #94a3b8',
                    borderRadius: 14,
                    padding: '14px 16px',
                    display: 'flex', alignItems: 'center', gap: 12,
                    textAlign: 'left', cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(15,23,42,.04)',
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: 'linear-gradient(135deg,#f97316,#fb7185)',
                    display: 'grid', placeItems: 'center',
                    fontSize: 22,
                  }}>📚</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                      Cassetto menu del cliente
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#8b5cf6', padding: '2px 6px', borderRadius: 6 }}>OWNER</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4, marginTop: 2 }}>
                      Carica foto/PDF del menù reale del cliente. L'agente li
                      elabora per il First Setup degli alimenti.
                    </div>
                  </div>
                  <div style={{ color: '#94a3b8' }}>›</div>
                </button>
              </div>
            )}

            </>)}
            {/* Lista categorie + items, visible for customers too. */}
            <div style={{ padding: '12px 16px 80px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '8px 4px 8px' }}>
                <h2 style={{ fontSize: 14, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>
                  Categorie {menuType === 'takeaway' ? 'asporto' : 'sala'} ({categories.length})
                </h2>
                {canEdit && <IonButton size="small" onClick={() => setShowAddCat(true)}>
                  + Categoria
                </IonButton>}
              </div>

              <IonButton fill="outline" expand="block" onClick={() => history.push(`/settings/menu/manage?view=${menuType}`)}>
                {canEdit ? 'Gestisci categorie e piatti' : 'Visualizza menù'}
              </IonButton>
              {categories.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  Nessuna categoria. Aggiungi la prima per iniziare.
                </div>
              ) : (
                categories.map((c) => {
                  const list = items.filter((i) => i.category_id === c.id);
                  return (
                    <div key={c.id} style={{
                      background: '#fff', borderRadius: 14, padding: '12px 14px',
                      marginBottom: 10,
                      boxShadow: '0 1px 2px rgba(15,23,42,.05), 0 2px 6px rgba(15,23,42,.04)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                          <span style={{ fontSize: 22 }}>{c.icon ?? '🍽️'}</span>
                          <div style={{ fontWeight: 700, color: '#0f172a' }}>{c.name}</div>
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>· {list.length} piatt{list.length === 1 ? 'o' : 'i'}</span>
                        </div>
                        {canEdit && <IonButton size="small" fill="clear" onClick={() => setAddItemCatId(c.id)}>
                          + Piatto
                        </IonButton>}
                      </div>
                      {list.map((it) => (
                        <div key={it.id} style={{
                          marginTop: 8, padding: '8px 10px',
                          background: '#f8fafc', borderRadius: 10,
                          display: 'flex', alignItems: 'flex-start', gap: 8,
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>
                              {it.name}
                              {it.out_of_stock && (
                                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#b91c1c', background: '#fee2e2', padding: '1px 8px', borderRadius: 999, textTransform: 'uppercase' }}>esaurito</span>
                              )}
                            </div>
                            {it.description && (
                              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{it.description}</div>
                            )}
                            {it.allergens && it.allergens.length > 0 && (
                              <div style={{ fontSize: 11, color: '#b45309', marginTop: 4, fontWeight: 600 }}>
                                {it.allergens.map((a) => ALLERGEN_LABELS_IT[a as Allergen] ?? a).join(' · ')}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums', textAlign: 'right', maxWidth: '45%' }}>
                            {menuItemPriceLabel(it)}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })
              )}
            </div>

            {!qrOnly && menuType === 'menu' && (<>
            {/* ── TAVOLI (T-026 + T-021 prep + T-028 restyle) ──────────── */}
            <div style={{ padding: '4px 16px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '8px 4px 8px' }}>
                <h2 style={{ fontSize: 14, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>
                  Tavoli ({tables.length})
                </h2>
              </div>

              {/* ── Area stampa (in alto, richiesta T-028) ─────────────── */}
              {loadingTables ? (
                <div style={{ padding: 16, textAlign: 'center' }}><IonSpinner name="dots" /></div>
              ) : tables.length === 0 ? (
                <div style={{
                  padding: '20px 16px', textAlign: 'center',
                  background: '#fff', borderRadius: 12,
                  boxShadow: '0 1px 2px rgba(15,23,42,.05), 0 2px 6px rgba(15,23,42,.04)',
                  color: '#64748b', fontSize: 13, lineHeight: 1.5,
                }}>
                  <div style={{ fontSize: 24, marginBottom: 6 }}>🪑</div>
                  Nessun tavolo ancora.<br />
                  Crea i tuoi tavoli qui sotto per generare i QR.
                </div>
              ) : (
                <>
                  {/* Stampa tutti */}
                  <div style={{
                    background: 'linear-gradient(135deg,#0066cc,#8b5cf6)',
                    color: '#fff', borderRadius: 12, padding: '12px 14px',
                    marginBottom: 10,
                    boxShadow: '0 4px 12px rgba(102,102,204,.25)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          🖨️ Stampa tutti i QR
                        </div>
                        <div style={{ fontSize: 12, opacity: 0.92, marginTop: 2 }}>
                          {tables.length} etichett{tables.length === 1 ? 'a' : 'e'} (tavoli{' '}
                          {tables.length === 1
                            ? tables[0].label
                            : `${tables[0].label}–${tables[tables.length - 1].label}`})
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowPrintAllConfirm(true)}
                        disabled={printing || printingAll || printablePrinters.length === 0}
                        style={{
                          background: '#fff', color: '#0066cc',
                          border: 'none', borderRadius: 10, padding: '10px 16px',
                          fontWeight: 700, fontSize: 13,
                          cursor: (printing || printingAll || printablePrinters.length === 0) ? 'not-allowed' : 'pointer',
                          opacity: (printing || printingAll || printablePrinters.length === 0) ? 0.6 : 1,
                          flexShrink: 0,
                        }}
                      >
                        {printingAll
                          ? `${printAllProgress.done}/${printAllProgress.total}`
                          : 'Stampa tutto'}
                      </button>
                    </div>
                  </div>

                  {/* Griglia tavoli con stampa singola */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                    gap: 8,
                  }}>
                    {tables.map((t) => (
                      <div key={t.id} style={{
                        background: '#fff', borderRadius: 12, padding: '14px 10px 10px',
                        boxShadow: '0 1px 2px rgba(15,23,42,.05), 0 2px 6px rgba(15,23,42,.04)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                      }}>
                        <div style={{
                          width: 48, height: 48, borderRadius: 12,
                          background: 'linear-gradient(135deg,#0066cc,#8b5cf6)',
                          color: '#fff', fontWeight: 800, fontSize: 18,
                          display: 'grid', placeItems: 'center',
                        }}>
                          {t.label}
                        </div>
                        <button
                          type="button"
                          onClick={() => { void printTableQr(t); }}
                          disabled={printing || printingAll || printablePrinters.length === 0}
                          style={{
                            width: '100%',
                            background: '#f1f5f9', color: '#0f172a',
                            border: 'none', borderRadius: 8, padding: '6px 8px',
                            fontSize: 11, fontWeight: 700,
                            cursor: (printing || printingAll) ? 'not-allowed' : 'pointer',
                            opacity: (printing || printingAll || printablePrinters.length === 0) ? 0.55 : 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                          }}
                        >
                          🖨️ Stampa QR
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {printablePrinters.length === 0 && tables.length > 0 && (
                <div style={{
                  marginTop: 10, padding: '10px 12px',
                  background: '#fef3c7', borderLeft: '3px solid #f59e0b',
                  borderRadius: 8, fontSize: 12, color: '#92400e', lineHeight: 1.45,
                }}>
                  Per stampare i QR dei tavoli configura prima una stampante BT in <b>Impostazioni → Stampante</b>.
                </div>
              )}

              {/* ── Crea nuovi tavoli (in basso, form minimo T-028) ────── */}
              <div style={{
                marginTop: 18,
                background: '#fff', borderRadius: 12, padding: '12px 14px',
                boxShadow: '0 1px 2px rgba(15,23,42,.05), 0 2px 6px rgba(15,23,42,.04)',
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  ➕ Aggiungi tavoli
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, lineHeight: 1.45 }}>
                  Quanti tavoli vuoi creare? Verranno numerati automaticamente
                  partendo dal prossimo libero.
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <IonInput
                    type="number"
                    inputmode="numeric"
                    min={1}
                    max={50}
                    placeholder="es. 10"
                    value={String(bulkCount)}
                    onIonInput={(ev) => {
                      const v = Number(ev.detail.value ?? 1);
                      if (Number.isFinite(v)) setBulkCount(Math.max(1, Math.min(50, Math.round(v))));
                    }}
                    style={{
                      flex: 1, fontSize: 14, textAlign: 'center',
                      '--padding-start': '10px', '--padding-end': '10px',
                      '--padding-top': '10px', '--padding-bottom': '10px',
                      '--background': '#f1f5f9', '--border-radius': '10px',
                    } as React.CSSProperties}
                  />
                  <IonButton
                    size="small" fill="solid"
                    disabled={savingTable || bulkCount < 1}
                    onClick={() => { void addTablesBulk(); }}
                    style={{ '--border-radius': '10px', flexShrink: 0 } as React.CSSProperties}
                  >
                    {savingTable
                      ? <IonSpinner name="dots" />
                      : `+ Crea ${bulkCount === 1 ? 'tavolo' : `${bulkCount} tavoli`}`}
                  </IonButton>
                </div>
              </div>
            </div>

            {/* T-028: modale conferma "Stampa tutti i QR tavolo" */}
            <IonModal
              isOpen={showPrintAllConfirm}
              onDidDismiss={() => setShowPrintAllConfirm(false)}
              breakpoints={[0, 1]}
              initialBreakpoint={1}
            >
              <div style={{ padding: '20px 22px 28px' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>
                  Stampare {tables.length} etichett{tables.length === 1 ? 'a' : 'e'}?
                </div>
                <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.55, marginBottom: 14 }}>
                  Stai per stampare <b>{tables.length} etichette QR</b> in sequenza
                  {tables.length > 1 && (
                    <> (tavoli <b>{tables[0].label}–{tables[tables.length - 1].label}</b>)</>
                  )}.
                  Userai ~{tables.length} etichett{tables.length === 1 ? 'a' : 'e'} del rotolo.
                  <br /><br />
                  Sei sicuro?
                </div>
                <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
                  <IonButton
                    expand="block"
                    color="primary"
                    onClick={() => { void printAllTableQrs(); }}
                    style={{ '--border-radius': '12px' } as React.CSSProperties}
                  >
                    🖨️ Stampa tutto
                  </IonButton>
                  <IonButton
                    expand="block"
                    fill="clear"
                    onClick={() => setShowPrintAllConfirm(false)}
                  >
                    Annulla
                  </IonButton>
                </div>
              </div>
            </IonModal>
            </>)}

            {/* QR Modal — T-053: toggle Menù/Aperitivo + share */}
            <IonModal isOpen={showQr} onDidDismiss={() => setShowQr(false)}>
              <IonHeader>
                <IonToolbar>
                  <IonTitle>QR del menù</IonTitle>
                  <IonButtons slot="end">
                    <IonButton onClick={() => setShowQr(false)}>Chiudi</IonButton>
                  </IonButtons>
                </IonToolbar>
              </IonHeader>
              <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
                <div style={{ padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
                  {/* Segmented toggle Menù / Aperitivo */}
                  <div role="tablist" aria-label="Tipo di QR" style={{
                    display: 'flex', gap: 4, padding: 4, background: '#dbe3ec',
                    borderRadius: 12, width: '100%', maxWidth: 360,
                  }}>
                    {(business.menu_public_slug === 'lo-sperdicchio' ? ['menu', 'takeaway', 'aperitivo'] as const : ['menu', 'aperitivo'] as const).map((t) => (
                      <button
                        key={t}
                        role="tab"
                        aria-selected={qrTarget === t}
                        type="button"
                        onClick={() => setQrTarget(t)}
                        style={{
                          flex: 1, padding: '10px 12px', borderRadius: 8,
                          border: 'none', fontWeight: 600, fontSize: 13,
                          background: qrTarget === t ? '#fff' : 'transparent',
                          color: qrTarget === t ? '#0f172a' : '#64748b',
                          boxShadow: qrTarget === t ? '0 1px 3px rgba(15,23,42,.12)' : 'none',
                          cursor: 'pointer', transition: 'all .15s ease',
                        }}
                      >
                        {targetLabel(t)}
                      </button>
                    ))}
                  </div>

                  {qrDataUrl ? (
                    <img src={qrDataUrl} alt={`QR ${targetLabel(qrTarget)}`} style={{
                      width: '88%', maxWidth: 360, aspectRatio: '1', height: 'auto',
                      borderRadius: 18, background: '#fff', padding: 16,
                      boxShadow: '0 4px 16px rgba(15,23,42,.15)',
                    }}/>
                  ) : (
                    <div style={{ width: '88%', maxWidth: 360, aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <IonSpinner name="dots" />
                    </div>
                  )}

                  {/* Bottone Condividi — T-053 */}
                  <button
                    type="button"
                    onClick={() => { void shareQr(); }}
                    disabled={!qrDataUrl}
                    style={{
                      width: '88%', maxWidth: 360,
                      background: '#0f172a', color: '#fff',
                      border: 'none', borderRadius: 12, padding: '14px 18px',
                      fontSize: 15, fontWeight: 700, cursor: qrDataUrl ? 'pointer' : 'not-allowed',
                      opacity: qrDataUrl ? 1 : 0.5,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      boxShadow: '0 2px 8px rgba(15,23,42,.18)',
                    }}
                  >
                    <span style={{ fontSize: 18 }}>📤</span>
                    {qrOnly ? 'Salva o condividi QR' : 'Condividi (WhatsApp, Mail, …)'}
                  </button>

                  <div style={{ fontSize: 13, color: '#64748b', textAlign: 'center', lineHeight: 1.5, maxWidth: 360 }}>
                    {qrTarget === 'aperitivo'
                      ? 'Questo QR apre direttamente la scheda Aperitivo. Perfetto per stampare banner al bancone.'
                      : 'Punta lo smartphone su questo QR per aprire il menù pubblico del locale.'}
                    <br />
                    <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11, wordBreak: 'break-all' }}>
                      {qrUrl}
                    </span>
                  </div>
                  <canvas ref={qrCanvas} style={{ display: 'none' }} />
                </div>
              </IonContent>
            </IonModal>

            {/* T-026: Stampa QR su etichetta BT */}
            <IonModal isOpen={showPrint} onDidDismiss={() => setShowPrint(false)}>
              <IonHeader>
                <IonToolbar>
                  <IonTitle>Stampa QR del menu</IonTitle>
                  <IonButtons slot="end">
                    <IonButton onClick={() => setShowPrint(false)}>Chiudi</IonButton>
                  </IonButtons>
                </IonToolbar>
              </IonHeader>
              <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
                <div style={{ padding: 16 }}>
                  <div style={{
                    background: '#fff', borderRadius: 12, padding: '14px 16px',
                    boxShadow: '0 1px 2px rgba(15,23,42,.05), 0 2px 6px rgba(15,23,42,.04)',
                    marginBottom: 12,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Verrà stampato
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginTop: 4 }}>
                      QR alla massima dimensione possibile
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-all' }}>
                      {publicUrl}
                    </div>
                  </div>

                  {!devicePrintersLoaded ? (
                    <div style={{ padding: 24, textAlign: 'center' }}>
                      <IonSpinner name="dots" />
                    </div>
                  ) : printablePrinters.length === 0 ? (
                    <div style={{
                      padding: '14px 16px',
                      background: '#fef3c7',
                      borderLeft: '3px solid #f59e0b',
                      borderRadius: 10,
                      fontSize: 13, color: '#92400e', lineHeight: 1.45,
                    }}>
                      Nessuna stampante configurata su questo dispositivo.
                      <br />
                      Vai in <b>Impostazioni → Stampante</b> per aggiungerne una.
                    </div>
                  ) : (
                    <>
                      <IonItem>
                        <IonSelect
                          label="Stampante"
                          labelPlacement="stacked"
                          value={printPrinterId}
                          onIonChange={(ev) => setPrintPrinterId(String(ev.detail.value ?? ''))}
                        >
                          {printablePrinters.map((p) => (
                            <IonSelectOption key={p.id} value={p.id}>
                              {p.name} ({p.label_size_mm.width}×{p.label_size_mm.height}&nbsp;mm)
                            </IonSelectOption>
                          ))}
                        </IonSelect>
                      </IonItem>
                      <IonItem>
                        <IonInput
                          label="Copie"
                          labelPlacement="stacked"
                          type="number"
                          inputmode="numeric"
                          min="1"
                          max="99"
                          value={String(printCopies)}
                          onIonInput={(ev) => {
                            const v = Number(ev.detail.value ?? 1);
                            if (Number.isFinite(v)) setPrintCopies(v);
                          }}
                        />
                      </IonItem>
                      {(() => {
                        const printer = printablePrinters.find((p) => p.id === printPrinterId);
                        if (!printer) return null;
                        const W = printer.label_size_mm.width;
                        const H = printer.label_size_mm.height;
                        const qrSizeMm = Math.max(10, Math.min(W, H) - 3);
                        return (
                          <div style={{
                            margin: '12px 4px 0',
                            fontSize: 12, color: '#64748b', lineHeight: 1.5,
                          }}>
                            ✨ Su etichette <b>{W}×{H}&nbsp;mm</b> il QR sarà stampato a{' '}
                            <b style={{ color: '#0066cc' }}>~{qrSizeMm.toFixed(1)}&nbsp;mm</b>{' '}
                            (margine 1,5&nbsp;mm per lato), centrato.
                          </div>
                        );
                      })()}
                      <IonButton
                        expand="block"
                        style={{ marginTop: 20 }}
                        disabled={printing || !printPrinterId}
                        onClick={() => { void printQrLabel(); }}
                      >
                        {printing ? <IonSpinner name="dots" /> : '🖨️ Stampa ora'}
                      </IonButton>
                    </>
                  )}
                </div>
              </IonContent>
            </IonModal>

            {/* Add categoria modal */}
            <IonModal isOpen={showAddCat} onDidDismiss={() => setShowAddCat(false)}>
              <IonHeader>
                <IonToolbar>
                  <IonTitle>Nuova categoria</IonTitle>
                  <IonButtons slot="end">
                    <IonButton onClick={() => setShowAddCat(false)}>Annulla</IonButton>
                  </IonButtons>
                </IonToolbar>
              </IonHeader>
              <IonContent>
                <div style={{ padding: 16 }}>
                  <IonItem>
                    <IonLabel position="stacked">Nome</IonLabel>
                    <IonInput value={newCatName} onIonInput={(ev) => setNewCatName(String(ev.detail.value ?? ''))} placeholder="Es. Bevande" />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">Emoji (opzionale)</IonLabel>
                    <IonInput value={newCatIcon} onIonInput={(ev) => setNewCatIcon(String(ev.detail.value ?? ''))} maxlength={4} />
                  </IonItem>
                  <IonButton
                    expand="block"
                    style={{ marginTop: 16 }}
                    disabled={savingCat || !newCatName.trim()}
                    onClick={() => { void createCategory(); }}
                  >
                    {savingCat ? <IonSpinner name="dots" /> : 'Crea categoria'}
                  </IonButton>
                </div>
              </IonContent>
            </IonModal>

            {/* Add item modal */}
            <IonModal isOpen={addItemCatId !== null} onDidDismiss={() => setAddItemCatId(null)}>
              <IonHeader>
                <IonToolbar>
                  <IonTitle>Nuovo piatto</IonTitle>
                  <IonButtons slot="end">
                    <IonButton onClick={() => setAddItemCatId(null)}>Annulla</IonButton>
                  </IonButtons>
                </IonToolbar>
              </IonHeader>
              <IonContent>
                <div style={{ padding: 16 }}>
                  <IonItem>
                    <IonLabel position="stacked">Nome *</IonLabel>
                    <IonInput value={newItem.name} onIonInput={(ev) => setNewItem((s) => ({ ...s, name: String(ev.detail.value ?? '') }))} />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">Descrizione</IonLabel>
                    <IonTextarea
                      autoGrow
                      rows={2}
                      value={newItem.description}
                      onIonInput={(ev) => setNewItem((s) => ({ ...s, description: String(ev.detail.value ?? '') }))}
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">Prezzo (€) *</IonLabel>
                    <IonInput inputmode="decimal" placeholder="12,50" value={newItem.price} onIonInput={(ev) => setNewItem((s) => ({ ...s, price: String(ev.detail.value ?? '') }))} />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">Destinazione comanda</IonLabel>
                    <IonSelect
                      value={newItem.station}
                      onIonChange={(ev) => setNewItem((s) => ({ ...s, station: (ev.detail.value as 'cucina' | 'bar') ?? 'cucina' }))}
                    >
                      <IonSelectOption value="cucina">Cucina</IonSelectOption>
                      <IonSelectOption value="bar">Bar / Cassa</IonSelectOption>
                    </IonSelect>
                  </IonItem>
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                      Allergeni (Reg. UE 1169/2011)
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {ALLERGEN_VALUES.map((a) => {
                        const sel = newItem.allergens.includes(a);
                        return (
                          <IonChip
                            key={a}
                            color={sel ? 'warning' : 'medium'}
                            outline={!sel}
                            onClick={() => setNewItem((s) => ({
                              ...s,
                              allergens: sel ? s.allergens.filter((x) => x !== a) : [...s.allergens, a],
                            }))}
                          >
                            <IonLabel>{ALLERGEN_LABELS_IT[a]}</IonLabel>
                          </IonChip>
                        );
                      })}
                    </div>
                  </div>
                  <IonButton
                    expand="block"
                    style={{ marginTop: 20 }}
                    disabled={savingItem || !newItem.name.trim()}
                    onClick={() => { void createItem(); }}
                  >
                    {savingItem ? <IonSpinner name="dots" /> : 'Crea piatto'}
                  </IonButton>
                </div>
              </IonContent>
            </IonModal>
          </>
        )}

        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={2000}
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
    </IonPage>
  );
}
